import {
	Box,
	Button,
	FormControl,
	IconButton,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateProfile } from '../../../features/authSlice'
import { toastHelper } from '../../../util/toastHelper'
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import styles from './index.module.scss'

export const SocialProfilesCard = ({
	formData,
	setFormData,
	cardStyle,
	editable,
}) => {
	const dispatch = useDispatch()
	const [editing, setEditing] = useState(false)

	const isHttps = (url) => /^https?:\/\//i.test(url?.trim())

	const validHost = (url, allowedHosts) => {
		try {
		const u = new URL(isHttps(url) ? url : `https://${url}`)
		const host = u.hostname.toLowerCase()
		return allowedHosts.some((h) => host === h || host.endsWith(`.${h}`))
		} catch {
		return false
		}
	}

    const isValidUrl = (string) => {
        try {
            const url = new URL(string);
            return url.protocol === "http:" || url.protocol === "https:";
        } catch (error) {
            return false;
        }
    }

	const validateSocialLinks = () => {
		const { instagramLink, tiktokLink, facebookLink, websiteLink } = formData

		if (instagramLink?.trim()) {
		if (!validHost(instagramLink, ['instagram.com'])) {
			toastHelper('Instagram link must be an instagram.com URL', 'error')
			return false
		}
		}
		if (tiktokLink?.trim()) {
		if (!validHost(tiktokLink, ['tiktok.com'])) {
			toastHelper('TikTok link must be a tiktok.com URL', 'error')
			return false
		}
		}
		if (facebookLink?.trim()) {
		if (!validHost(facebookLink, ['facebook.com'])) {
			toastHelper('Facebook link must be a facebook.com URL', 'error')
			return false
		}
		}
        if(websiteLink && !isValidUrl(websiteLink)){
            toastHelper('Invalid url', 'error')
            return false
        }
		return true
	}

	let textFieldStyles = {
		bgcolor: 'white',
		margin: '0',
		'.MuiInputBase-input': {
			padding: '10px 10px 10px 0',
			fontSize: '14px',
			fontWeight: '400',
			color: '#313131',
		},

		'& label.MuiInputLabel-root': {
			color: '#7ca6bd',
		},
		'& .MuiInputAdornment-root': {
			marginRight: '5px',
			'& img': {
				width: '16px',
				height: '16px',
			},
		},

		'& .MuiInputBase-input.Mui-disabled': {
			color: '#313131',
			WebkitTextFillColor: 'black',
			opacity: 1,
			padding: '10px 10px 10px 0',
			fontSize: '14px',
			fontWeight: '400',
		},
		'& .MuiOutlinedInput-root': {
			borderRadius: '50px',
			padding: '0 10px !important',
			'& > fieldset': {
				borderColor: '#DFE4EB !important',
			},
		},
	}

	const handleChange = (e, field) => {
		setFormData({ ...formData, [field]: e.target.value })
	}

	const handleSave = () => {
    if (!validateSocialLinks()) return
    dispatch(
      updateProfile({
        instagramLink: formData.instagramLink?.trim() || '',
        tiktokLink: formData.tiktokLink?.trim() || '',
        facebookLink: formData.facebookLink?.trim() || '',
        websiteLink: (formData.websiteLink || '').trim(),
      })
    )
    setEditing(false)
  }

	return (
		<div
			sx={cardStyle}
			className={`${styles.cardWrp} ${styles.socialInfo} `}
		>
			<div className={styles.infoHeader}>
				<Typography
					variant="h5"
					gutterBottom
					sx={{ fontWeight: 'bold' }}
				>
					Social Profiles
				</Typography>
				{editable && (
					<Box>
						<IconButton
							component="label"
							className={styles.editIcon}
							disableRipple
							onClick={() => setEditing(true)}
						>
							<img
								src="/images/edit-icon.svg"
								alt="business pic"
							/>
						</IconButton>
						{editing && <Button onClick={handleSave}>Save</Button>}
					</Box>
				)}
			</div>
			<FormControl
				variant="standard"
				className={styles.socialInputFieldWrp}
			>
				<TextField
					className={styles.socialInputField}
					disabled={!editing}
					// label="Instagram"
					placeholder="https://www.instagram.com/handle"
					sx={textFieldStyles}
					value={formData.instagramLink || ''}
					onChange={(e) => handleChange(e, 'instagramLink')}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<img
										src="/images/instagram.svg"
										alt="instagram.svg"
									/>
								</InputAdornment>
							),
						},
					}}
				/>
				<TextField
					className={styles.socialInputField}
					disabled={!editing}
					// label="Tiktok"
					placeholder="https://www.tiktok.com/handle"
					sx={textFieldStyles}
					value={formData.tiktokLink || ''}
					onChange={(e) => handleChange(e, 'tiktokLink')}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<img
										src="/images/tiktok.svg"
										alt="tiktok.svg"
									/>
								</InputAdornment>
							),
						},
					}}
				/>
				<TextField
					className={styles.socialInputField}
					disabled={!editing}
					// label="Facebook"
					placeholder="https://www.facebook.com/handle"
					sx={textFieldStyles}
					value={formData.facebookLink || ''}
					onChange={(e) => handleChange(e, 'facebookLink')}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<img
										src="/images/facebook.svg"
										alt="facebook.svg"
									/>
								</InputAdornment>
							),
						},
					}}
				/>
				<TextField
					className={styles.socialInputField}
					disabled={!editing}
					placeholder="https://www.yourBusinessWebsite"
					sx={textFieldStyles}
					value={formData.websiteLink || ''}
					onChange={(e) => handleChange(e, 'websiteLink')}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<LanguageOutlinedIcon sx={{ color: '#7ca6bd' }} />
								</InputAdornment>
							),
						},
					}}
				/>
			</FormControl>
		</div>
	)
}
